import React, { useEffect, useState, useRef } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import mondaySdk from 'monday-sdk-js'
import RelationController from './views/RelationController'
import Login from './views/Login'
import userService from './services/userService'
import Footer from './cmps/Footer'
import Header from './cmps/Header'
import RelationCreator from './views/RelationCreator'
import boardService from './services/boardService'
import { mondayService } from './services/mondayService'
import useMondaySettings from './hooks/useMondaySettings'
import { Provider, ErrorBoundary } from '@rollbar/react' // Provider imports 'rollbar'
import ObjectivesTree from './views/ObjectivesTree'
import ExpiredLicense from './cmps/ExpiredLicense'
import GroupsTree from './views/GroupsTree'

const domain = import.meta.env.PROD ? 'https://esl-okr-management-production.up.railway.app' : ''

const monday = mondaySdk()

export default function App() {
  const boardsRequestId = useRef(0)

  const [context, setContext] = useState()
  const [currentBoarrdId, setCurrentBoarrdId] = useState()
  const [userId, setUserId] = useState()
  const [userEmail, setUserEmail] = useState()
  const [user, setUser] = useState()
  const [isEnterprise, setIsEnterprise] = useState(false)
  const [loading, setLoading] = useState(true)
  const [userDomain, setUserDomain] = useState()
  const [boards, setBoards] = useState()
  const [relations, setRelations] = useState()
  const [relationsToDisplay, setRelationsToDisplay] = useState()
  const [darkMode, setDarkMode] = useState(false)
  const [workspaceId, setWorkspaceId] = useState(null)
  const [sessionToken, setSessionToken] = useState(null)
  const [isLoadingBoards, setIsLoadingBoards] = useState(false)
  const { isShowCurrWorkspace } = useMondaySettings(sessionToken, user, monday)

  useEffect(() => {
    getContext()
    getThemeMode()
  }, [])

  useEffect(() => {
    if ((isShowCurrWorkspace === false || isShowCurrWorkspace === true) && workspaceId && userId) {
      const newRequestId = Date.now()
      boardsRequestId.current = newRequestId // Update the current request ID
      onLoadingBoards(workspaceId, isShowCurrWorkspace, userId, newRequestId)
    }
  }, [isShowCurrWorkspace, workspaceId, userId])

  const onLoadingBoards = async (workspaceId, isShowCurrWorkspace, userId, currentRequestId) => {
    setBoards([])
    try {
      setIsLoadingBoards(true)

      const boards = await mondayService.getBoards(workspaceId, isShowCurrWorkspace, userId, monday)
      // Ignore if this is not the latest request
      if (currentRequestId !== boardsRequestId.current) return
      setBoards(boards)
    } catch (err) {
      console.log('file: App.jsx:62 -> err:', err)
    } finally {
      if (currentRequestId === boardsRequestId.current) {
        setIsLoadingBoards(false)
      }
    }
  }

  useEffect(() => {
    if (context) {
      setUserId(Number(context?.user?.id))
    }
  }, [context])

  useEffect(() => {
    userId && mondayService.getSlug(userId, setUserEmail, setUserDomain, monday)
  }, [userId])

  useEffect(() => {
    userEmail && userDomain && sessionToken && checkForUsers()
  }, [userEmail, userDomain, sessionToken])

  useEffect(() => {
    setIsEnterprise(user?.license === 'enterprise')
    user && (location.pathname !== "/groupsTree") && monday.execute('openSettings')
  }, [user])

  useEffect(() => {
    if (user && sessionToken) {
      getBoardRelations()
    }
  }, [user, sessionToken])

  useEffect(() => {
    if (user && sessionToken) {
      onSetRelationsToDisplay()
    }
  }, [isShowCurrWorkspace, relations])

  const getRollbarConfig = () => {
    const rollbarConfig = {
      accessToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN,
      environment: import.meta.env.MODE,
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: {
        person: {
          userEmail,
          userId,
        },
      },
    }

    return rollbarConfig
  }

  const onSetRelationsToDisplay = async () => {
    if (isShowCurrWorkspace) {
      const boardRelationsData = await mondayService.filterBoardsByWorkspace(workspaceId, relations, monday)
      setRelationsToDisplay(boardRelationsData)
    } else {
      setRelationsToDisplay(relations)
    }
  }

  const getBoardRelations = async () => {
    const boardRelations = await boardService.getUserBoards(user.userDomain, sessionToken)
    setRelations(boardRelations.data)

    onSetIsLoading(false)
  }

  const checkForUsers = async () => {
    onSetIsLoading(true)
    const user = await userService.getUserByEmail(userEmail, userDomain, sessionToken)
    if (user) {
      const userLicense = user.account.license.split('-')[0] === 'enterprise' ? 'enterprise' : user.account.license
      user.license = userLicense
      setUser(user)
    }
    onSetIsLoading(false)
  }

  const getContext = async () => {
    const contextData = await monday.get('context')
    setWorkspaceId(contextData.data.workspaceId)
    setContext(contextData?.data)
    setCurrentBoarrdId(contextData.data.boardId)

    const sessionToken = await monday.get('sessionToken')
    setSessionToken(sessionToken.data)
  }

  const getThemeMode = () => {
    monday.listen('context', (res) => {
      if (res.data.theme === 'dark' || res.data.theme === 'night' || res.data.theme === 'hacker' || res.data.theme === 'black' || res.data.theme === 'hacker_theme') setDarkMode(true)
      else setDarkMode(false)
    })
  }

  const onTabBackEvent = async () => {
    window.location.reload()
    const user = await userService.getUserByEmail(userEmail, userDomain, sessionToken)
    setUser(user)
  }

  const onSetIsLoading = (isLoading) => {
    setLoading(isLoading)
  }

  if (!user)
    return (
      <main className={darkMode ? 'App dark-mode ' : 'App'}>
        <Login onTabBackEvent={onTabBackEvent} loading={loading} domain={domain} userEmail={userEmail} />
      </main>
    )

  if (user.license === 'free-trial-ended' || user.license === 'expired-enterprise' || user.license === 'no-license') return <ExpiredLicense userEmail={user.userEmail} license={user.license} />
  return (
    <Provider config={getRollbarConfig()}>
      <ErrorBoundary>
        <>
          <main className={darkMode ? 'App dark-mode ' : 'App'}>
            {user && (location.pathname !== "/groupsTree") && <Header user={user} userLicense={user.license} expirationDate={user.account.expirationDate} monday={monday} />}
            <Routes>
              <Route
                path="/"
                element={<RelationCreator sessionToken={sessionToken} userId={userId} user={user} isEnterprise={isEnterprise} loading={loading} setLoading={setLoading} monday={monday} boards={boards} relations={relations} setRelations={setRelations} isLoadingBoards={isLoadingBoards} />}
              />
              <Route exact path="/controller" element={<RelationController sessionToken={sessionToken} userId={userId} user={user} loading={loading || isLoadingBoards} boards={boards} relations={relationsToDisplay} setRelations={setRelations} monday={monday} />} />
              <Route exact path="/objectivesTree" element={<ObjectivesTree monday={monday} relations={relationsToDisplay} boardId={currentBoarrdId} boards={boards} />} />
              <Route exact path="/groupsTree" element={<GroupsTree monday={monday} relations={relationsToDisplay} userDomain={userDomain} />} />
            </Routes>
          </main>
          <Footer className={darkMode ? 'dark-mode ' : ''} />
        </>
      </ErrorBoundary>
    </Provider>
  )
}